import { LitElement, css, html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { msg, str } from '@lit/localize';
import { NAMESPACE } from '../../constants';

import { observer as resizeObserver } from '../../utils/resize';
import { setLocale, getLang } from '../../utils/locale';
import { remap, clamp } from '../../utils';

import styles from './slider.css';

function nearest(targetValue, valueList) {
  if (!Array.isArray(valueList) || valueList.length === 0) {
    return targetValue;
  }
  
  let closestValue = valueList[0];
  let minDiff = Math.abs(targetValue - closestValue);
  
  for (let i = 1; i < valueList.length; i++) {
    const currentDiff = Math.abs(targetValue - valueList[i]);
    if (currentDiff < minDiff) {
      minDiff = currentDiff;
      closestValue = valueList[i];
    }
  }
  
  return closestValue;
}

function atPercent(percent, array) {
  if (!Array.isArray(array) || array.length === 0) {
    return 0;
  }
  const maxIndex = array.length - 1;
  const calculatedIndex = Math.round((percent / 100) * maxIndex);
  const safeIndex = Math.min(Math.max(calculatedIndex, 0), maxIndex);
  return array[safeIndex];
}

function template(elem) {
  const {
    min, max, value, datalist, type = 'step', snap = false, 
  } = elem;
  const { dragging } = elem;

  let left = 0;
  let points = [];
  
  if (type === 'step' && Array.isArray(datalist) && datalist.length > 0) {
    const steps = datalist.length - 1;
    const targetIdx = datalist.indexOf(nearest(value, datalist));
    left = steps > 0 ? (targetIdx / steps) * 100 : 0;
    points = datalist.map((n, i) => steps > 0 ? (i / steps) * 100 : 0);
  } else if (snap && Array.isArray(datalist) && datalist.length > 0) {
    left = remap(+value, min, max, 0, 100);
    points = datalist.map((n) => remap(+n, min, max, 0, 100));
  } else {
    left = remap(+value, min, max, 0, 100);
  }
  
  return html`
    <div class="slider ${type}">
      <div class="knob ${dragging ? 'dragging' : ''}" style=${styleMap({ left: `${left}%` })}></div>
      <div class="markers">
        ${points.map(
          (point) => html`<div class="stop" style="${styleMap({ left: `${point}%` })}"></div>`,
        )}
      </div>
      <div class="progress" style="${styleMap({ width: `${left}%` })}"></div>
      <div class="track"></div>
    </div>
  `;
}

export default class AmountSlider extends LitElement {
  static styles = styles;

  static properties = {
    min: { type: Number },
    max: { type: Number },
    value: { type: Number },
    datalist: { type: Array },
    type: { type: String },
    defaultValue: { type: Number },
  };

  constructor() {
    super();
    this.min = 0;
    this.max = 100;
    this.value = 50;
    this.datalist = [];
    this.type = 'step';
    this.defaultValue = undefined;
  }

  setValueFromPercent(percent) {
    const {
      datalist, min, max, type = 'step', snap = false,
    } = this;

    let calculatedValue;
    if (type === 'step' && Array.isArray(datalist) && datalist.length > 0) {
      calculatedValue = atPercent(percent, datalist);
    } else if (snap && Array.isArray(datalist) && datalist.length > 0) {
      const rawVal = remap(percent, 0, 100, min, max);
      calculatedValue = nearest(rawVal, datalist);
    } else {
      calculatedValue = remap(percent, 0, 100, min, max);
    }
    this.setValue(calculatedValue);
  }

  setValue(newValue) {
    const changeEvent = new CustomEvent('change', {
      detail: { value: newValue },
      bubbles: true,
      composed: true,  // allows event to cross Shadow DOM boundaries
      cancelable: true,
    });
    this.dispatchEvent(changeEvent);
    if (!changeEvent.defaultPrevented) {
      this.value = newValue;
    }
  }

  firstUpdated() {
    this.initializeValue();
  }

  updated(changedProperties) {
    if (changedProperties.has('datalist') && this.needsReinitialization()) {
      this.initializeValue();
    }
  }

  needsReinitialization() {
    return this.value === undefined || 
           this.value === null || 
           this.value === 0;
  }

  initializeValue() {
    if (this.hasValidValue(this.value)) {
      return;
    }

    let initialValue;

    if (this.hasValidValue(this.defaultValue)) {
      initialValue = Number(this.defaultValue);
    } else if (Array.isArray(this.datalist) && this.datalist.length > 0) {
      const middleIndex = Math.floor(this.datalist.length / 2);
      initialValue = this.datalist[middleIndex];
    } else if (this.min !== undefined && this.max !== undefined) {
      initialValue = Math.floor((this.min + this.max) / 2);
    }

    if (initialValue !== undefined) {
      this.value = Number(initialValue);
      console.log('[Slider] Value initialized to:', this.value);
    }
  }

  hasValidValue(val) {
    return val !== undefined && 
           val !== null && 
           val !== 0 && 
           val !== '' && 
           !isNaN(val);
  }

  connectedCallback() {
    super.connectedCallback();

    let startX;
    let bounds;

    const handleDrag = (e) => {
      this.dragging = e.type === 'pointermove';

      const deltaX = e.pageX - startX;
      const positionX = clamp(deltaX - bounds.left + startX, 0, bounds.width);
      const percentage = bounds.width > 0 ? (positionX / bounds.width) * 100 : 0;

      this.setValueFromPercent(percentage);
      this.requestUpdate();
    };

    const handleDragEnd = (e) => {
      this.dragging = false;
      this.requestUpdate();
      document.removeEventListener('pointermove', handleDrag, { capture: true });
      document.removeEventListener('pointerup', handleDragEnd, { capture: true });
    };

    const handleDragStart = (e) => {
      e.preventDefault();

      startX = e.layerX;
      bounds = this.getBoundingClientRect();
      handleDrag(e);

      document.addEventListener('pointermove', handleDrag, { capture: true });
      document.addEventListener('pointerup', handleDragEnd, { capture: true });
    };

    this.addEventListener('pointerdown', handleDragStart, { capture: true });
  }

  render() {
    return template(this);
  }
}

customElements.define(`${NAMESPACE}-loan-slider`, AmountSlider);